import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { X, Cookie } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const COOKIE_CONSENT_KEY = "tracelink_cookie_consent";

export const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Check localStorage first for quick response
    const localConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (localConsent) {
      setShowBanner(false);
      return;
    }

    // Check if user has already consented (for logged in users)
    const checkConsent = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.user) {
        const { data } = await supabase
          .from("user_preferences")
          .select("cookie_consent")
          .eq("user_id", session.user.id)
          .maybeSingle();

        if (data?.cookie_consent) {
          localStorage.setItem(COOKIE_CONSENT_KEY, "true");
          setShowBanner(false);
          return;
        }
      }

      // Show banner after a short delay for better UX
      setTimeout(() => setShowBanner(true), 1000);
    };

    checkConsent();
  }, []);

  const handleAccept = async (marketingConsent: boolean = false) => {
    setIsLoading(true);
    
    // Save to localStorage immediately
    localStorage.setItem(COOKIE_CONSENT_KEY, "true");
    
    // Save to database for logged in users
    const { data: { session } } = await supabase.auth.getSession();
    
    if (session?.user) {
      await supabase
        .from("user_preferences")
        .upsert({
          user_id: session.user.id,
          cookie_consent: true,
          cookie_consent_date: new Date().toISOString(),
          marketing_consent: marketingConsent,
        }, { onConflict: "user_id" });
    }
    
    setShowBanner(false);
    setIsLoading(false);
  };

  const handleDecline = () => {
    // Still set essential cookies consent but decline analytics/marketing
    localStorage.setItem(COOKIE_CONSENT_KEY, "essential");
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-slide-up">
      <div className="container max-w-4xl mx-auto">
        <div className="relative bg-card/95 backdrop-blur-xl border border-border rounded-2xl p-6 shadow-2xl">
          {/* Close button */}
          <button
            onClick={handleDecline}
            className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Close cookie banner"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            {/* Icon */}
            <div className="p-3 rounded-xl bg-primary/10 shrink-0">
              <Cookie className="w-6 h-6 text-primary" />
            </div>

            {/* Content */}
            <div className="flex-1 pr-8 sm:pr-0">
              <h3 className="font-semibold text-foreground mb-1">Cookie Preferences</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                We use cookies to enhance your experience, analyze site traffic, and for marketing purposes. 
                By clicking "Accept All", you consent to our use of cookies. Read our{" "}
                <Link to="/privacy" className="text-primary hover:underline">
                  Privacy Policy
                </Link>{" "}
                to learn more.
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={handleDecline}
                className="rounded-xl"
              >
                Essential Only
              </Button>
              <Button
                size="sm"
                onClick={() => handleAccept(true)}
                disabled={isLoading}
                className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl shadow-[0_0_20px_hsl(var(--glow-primary)/0.3)]"
              >
                {isLoading ? "Saving..." : "Accept All"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
